import {
  flow,
  Instance,
  SnapshotIn,
} from 'mobx-state-tree';
import moment from 'moment-timezone';
import Hades from '@services/Hades';
import { IBenefitServices } from '@stores/ApplicationInterfaces';
import BenefitService from '@stores/BenefitService';
import BenefitTag from '@stores/BenefitTag';
import { BenefitTypes, trackBenefitEntityCreated, trackBenefitEntityDeleted, trackBenefitEntityEdited } from '@utils/analytics';

export default {
  actions: (self: { hades: Hades; benefitServices: IBenefitServices }) => ({
    getBenefitServicesList: flow(function* getBenefitServicesList(
      vendorId,
    ) {
      const result: SnapshotIn<typeof BenefitService>[] = yield self.hades.request(
        'benefit.serviceList',
        {
          vendorId,
          allRecords: true,
        },
      );

      self.benefitServices.clear();

      result.forEach((service: any) => {
        self.benefitServices.set(service.id, {
          ...service,
          criteria: typeof service.criteria === 'object'
          ? JSON.stringify(service.criteria)
          : service.criteria,
        });
      });
    }),

    createBenefitService: flow(function* createBenefitService(
      {
        name,
        description,
        eligibilityInfo,
        hoursOfOperation,
        instructions,
        whatToExpect,
        registrationInstructions,
        excludedFromOrgs,
        criteria,
        tags,
        vendorId,
        activeFrom,
        activeTo,
      }
    ) {
      const serviceData = {
        name,
        description,
        eligibilityInfo,
        hoursOfOperation,
        instructions,
        whatToExpect,
        registrationInstructions,
        excludedFromOrgs,
        criteria,
        vendorId,
        tagIds: tags.map((tag: Instance<typeof BenefitTag>) => tag.id),
        activeFrom: moment(activeFrom).utc(),
        activeTo: moment(activeTo).utc(),
      };

      const result: SnapshotIn<typeof BenefitService> = yield self.hades.request(
        'benefit.serviceCreate',
        serviceData,
      );

      if (result) {
        self.benefitServices.set(`${result.id}`, result);
      }

      trackBenefitEntityCreated(BenefitTypes.Service, serviceData);

      return result;
    }),

    updateBenefitService: flow(function* updateBenefitService(
      {
        id: serviceId,
        name,
        description,
        eligibilityInfo,
        hoursOfOperation,
        instructions,
        whatToExpect,
        registrationInstructions,
        excludedFromOrgs,
        criteria,
        tags,
        vendorId,
        activeFrom,
        activeTo,
      }
    ) {
      const serviceData = {
        serviceId,
        name,
        description,
        eligibilityInfo,
        hoursOfOperation,
        instructions,
        whatToExpect,
        registrationInstructions,
        criteria,
        vendorId,
        excludedFromOrgs,
        tagIds: tags.map((tag: Instance<typeof BenefitTag>) => tag.id),
        activeFrom: moment(activeFrom).utc(),
        activeTo: moment(activeTo).utc(),
      };

      const result: SnapshotIn<typeof BenefitService> = yield self.hades.request(
        'benefit.serviceUpdate',
        serviceData,
      );

      if (result) {
        self.benefitServices.set(`${result.id}`, result);
      }

      trackBenefitEntityEdited(BenefitTypes.Service, serviceData);

      return result;
    }),

    deleteBenefitService: flow(function* deleteBenefitService(
      {
        id: serviceId,
        vendorId,
      }
    ) {
      const serviceData = {
        serviceId,
        vendorId,
      };

      const result: SnapshotIn<typeof BenefitService> = yield self.hades.request(
        'benefit.serviceDelete',
        serviceData,
      );

      if (result) {
        self.benefitServices.delete(`${serviceId}`);
      }

      trackBenefitEntityDeleted(BenefitTypes.Service, serviceData);

      return result;
    }),
  }),
};
